import { useState, useCallback, useMemo } from 'react'
import { useApiFetch } from '../hooks/useApiFetch'
import { useApiMutation } from '../hooks/useApiMutation'
import { useToast } from '../components/ToastContainer'
import { apiFetch, webUrl } from '../lib/api'
import { Button } from '../ui/Button'
import { Input } from '../ui/Input'
import { Select } from '../ui/Select'
import { Textarea } from '../ui/Textarea'
import { Modal } from '../ui/Modal'
import { Badge } from '../ui/Badge'
import { Spinner } from '../ui/Spinner'
import { FileUpload } from '../ui/FileUpload'
import { AdminTable } from '../ui/AdminTable'
import type { TableColumn } from '../ui/Table'
import styles from './SharedVideos.module.css'

// ── 타입 ──

interface SharedAsset {
  id: string
  type: string
  name: string
  slug: string
  category: string
  tags: string[]
  description: string | null
  s3Key: string | null
  fileUrl: string | null
  fileSize: number | null
  mimeType: string | null
  metadata: Record<string, unknown>
  sortOrder: number
  isActive: boolean
  createdAt: string
  updatedAt: string
}

interface AssetsResponse {
  assets: SharedAsset[]
}

// ── 상수 ──

const CATEGORY_OPTIONS = [
  { value: 'intro', label: '인트로' },
  { value: 'ceremony', label: '예식' },
  { value: 'scenery', label: '풍경' },
  { value: 'effect', label: '효과' },
  { value: 'other', label: '기타' },
]

const CATEGORY_FILTER_OPTIONS = [
  { value: '', label: '전체' },
  ...CATEGORY_OPTIONS,
]

const CATEGORY_BADGE_COLORS: Record<string, 'blue' | 'green' | 'red' | 'yellow'> = {
  intro: 'blue',
  ceremony: 'green',
  scenery: 'yellow',
  effect: 'red',
  other: 'blue',
}

const CATEGORY_LABELS: Record<string, string> = Object.fromEntries(
  CATEGORY_OPTIONS.map((o) => [o.value, o.label]),
)

function formatFileSize(bytes: number | null): string {
  if (!bytes) return '-'
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

// ── 메인 컴포넌트 ──

export function SharedVideos() {
  const { addToast } = useToast()

  const { data, isLoading, refetch } = useApiFetch<AssetsResponse>(
    '/api/admin/shared-assets?type=video&includeInactive=true',
  )
  const assetsList = data?.assets ?? []

  const [categoryFilter, setCategoryFilter] = useState('')
  const filteredAssets = useMemo(() => {
    if (!categoryFilter) return assetsList
    return assetsList.filter((a) => a.category === categoryFilter)
  }, [assetsList, categoryFilter])

  // 업로드
  const [uploading, setUploading] = useState(false)
  const [uploadKey, setUploadKey] = useState(0)

  // 편집 모달
  const [editingAsset, setEditingAsset] = useState<SharedAsset | null>(null)
  const [formName, setFormName] = useState('')
  const [formSlug, setFormSlug] = useState('')
  const [formCategory, setFormCategory] = useState('other')
  const [formTags, setFormTags] = useState('')
  const [formDescription, setFormDescription] = useState('')
  const [formSortOrder, setFormSortOrder] = useState(0)

  const updateMut = useApiMutation<SharedAsset, Record<string, unknown>>('/api/admin/shared-assets', 'PUT')
  const toggleMut = useApiMutation<unknown, Record<string, unknown>>('/api/admin/shared-assets', 'PATCH')

  // 파일 업로드 → 자동 등록
  const handleUpload = useCallback(async (files: File[]) => {
    if (files.length === 0) return
    setUploading(true)
    try {
      for (const file of files) {
        const formData = new FormData()
        formData.append('file', file)
        await apiFetch<SharedAsset>(
          '/api/admin/shared-assets/upload?type=video',
          { method: 'POST', body: formData },
        )
      }
      addToast({ type: 'success', message: `${files.length}개 영상이 등록되었습니다` })
      setUploadKey((k) => k + 1)
      refetch()
    } catch (err: any) {
      addToast({ type: 'error', message: err.message || '업로드 실패' })
    } finally {
      setUploading(false)
    }
  }, [addToast, refetch])

  // 편집
  const openEdit = useCallback((asset: SharedAsset) => {
    setEditingAsset(asset)
    setFormName(asset.name)
    setFormSlug(asset.slug)
    setFormCategory(asset.category)
    setFormTags((asset.tags ?? []).join(', '))
    setFormDescription(asset.description ?? '')
    setFormSortOrder(asset.sortOrder)
  }, [])

  const handleSave = useCallback(async () => {
    if (!editingAsset) return
    const tags = formTags.split(',').map(t => t.trim()).filter(Boolean)
    const result = await updateMut.trigger({
      name: formName,
      slug: formSlug,
      category: formCategory,
      tags,
      description: formDescription || null,
      sortOrder: formSortOrder,
    }, { pathOverride: `/api/admin/shared-assets/${editingAsset.id}` })
    if (result) {
      addToast({ type: 'success', message: '수정되었습니다' })
      setEditingAsset(null)
      refetch()
    }
  }, [editingAsset, formName, formSlug, formCategory, formTags, formDescription, formSortOrder, updateMut, addToast, refetch])

  const handleToggle = useCallback(async (row: SharedAsset, _newValue: boolean) => {
    const result = await toggleMut.trigger({}, { pathOverride: `/api/admin/shared-assets/${row.id}/toggle` })
    if (result === null) throw new Error('토글 변경에 실패했습니다')
    refetch()
  }, [toggleMut, refetch])

  const handleDelete = useCallback(async (row: SharedAsset) => {
    await apiFetch(`/api/admin/shared-assets/${row.id}`, { method: 'DELETE' })
    refetch()
  }, [refetch])

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '64px' }}>
        <Spinner />
      </div>
    )
  }

  const columns: TableColumn<SharedAsset>[] = [
    {
      key: 'name',
      label: '이름',
      render: (row) => (
        <>
          <div style={{ fontWeight: 600 }}>{row.name}</div>
          <div style={{ fontSize: '12px', color: '#6b7280' }}>{row.slug}</div>
        </>
      ),
    },
    {
      key: 'category',
      label: '카테고리',
      width: '110px',
      render: (row) => (
        <Badge color={CATEGORY_BADGE_COLORS[row.category] ?? 'blue'}>
          {CATEGORY_LABELS[row.category] ?? row.category}
        </Badge>
      ),
    },
    {
      key: 'fileSize',
      label: '크기',
      width: '80px',
      render: (row) => <span className={styles.fileInfo}>{formatFileSize(row.fileSize)}</span>,
    },
    {
      key: 'player',
      label: '미리보기',
      width: '200px',
      render: (row) => row.fileUrl ? (
        <video controls className={styles.videoPlayer} preload="none">
          <source src={row.fileUrl} type={row.mimeType || 'video/mp4'} />
        </video>
      ) : null,
    },
    {
      key: 'url',
      label: 'URL',
      width: '60px',
      render: (row) => (
        <button
          type="button"
          className={styles.copyUrlBtn}
          onClick={() => {
            navigator.clipboard.writeText(webUrl(`/assets/video/${row.slug}`))
            addToast({ type: 'success', message: 'URL이 복사되었습니다' })
          }}
          aria-label="URL 복사"
        >
          📋
        </button>
      ),
    },
  ]

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 700 }}>영상 에셋</h2>
        <Select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)} options={CATEGORY_FILTER_OPTIONS} size="sm" />
      </div>

      {/* 업로드 영역 */}
      <div style={{ marginBottom: '24px' }}>
        <FileUpload
          key={uploadKey}
          accept=".mp4,.webm"
          placeholder=".mp4, .webm 파일을 드래그하거나 클릭 (자동 등록, 최대 100MB)"
          onChange={handleUpload}
          loading={uploading}
          multiple
        />
      </div>

      <AdminTable<SharedAsset>
        columns={columns}
        data={filteredAssets}
        rowKey={(row) => row.id}
        emptyMessage="등록된 영상이 없습니다"
        hoverable
        toggles={[{ label: '활성', onToggle: handleToggle, getValue: (row) => row.isActive }]}
        actions={[{ label: '편집', onClick: openEdit }]}
        delete={{
          message: (row) => `"${row.name}" 영상을 삭제하시겠습니까?`,
          onDelete: handleDelete,
          successMessage: '삭제되었습니다',
        }}
      />

      {/* 편집 모달 */}
      <Modal open={!!editingAsset} onClose={() => setEditingAsset(null)} title="영상 편집" size="lg">
        <div className={styles.formGrid}>
          {editingAsset?.fileUrl && (
            <div className={styles.formFull}>
              <video controls style={{ width: '100%', maxHeight: '300px' }} preload="none">
                <source src={editingAsset.fileUrl} type={editingAsset.mimeType || 'video/mp4'} />
              </video>
            </div>
          )}
          <Input label="이름" value={formName} onChange={(e) => setFormName(e.target.value)} />
          <Input label="Slug" value={formSlug} onChange={(e) => setFormSlug(e.target.value)} />
          <Select label="카테고리" value={formCategory} onChange={(e) => setFormCategory(e.target.value)} options={CATEGORY_OPTIONS} />
          <Input label="정렬 순서" type="number" value={String(formSortOrder)} onChange={(e) => setFormSortOrder(Number(e.target.value))} />
          <div className={styles.formFull}>
            <Input label="태그 (쉼표 구분)" value={formTags} onChange={(e) => setFormTags(e.target.value)} placeholder="인트로, 드론" />
          </div>
          <div className={styles.formFull}>
            <Textarea label="설명" value={formDescription} onChange={(e) => setFormDescription(e.target.value)} rows={2} />
          </div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 20 }}>
          <Button variant="secondary" onClick={() => setEditingAsset(null)}>취소</Button>
          <Button onClick={handleSave} disabled={!formName || !formSlug || updateMut.isLoading}>수정</Button>
        </div>
      </Modal>
    </div>
  )
}
